// Hook personalizado para gestionar ofertas
// Proporciona funciones para obtener, crear, actualizar y confirmar ofertas

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getAllOffers,
  getOffer,
  createOffer,
  updateOffer,
  confirmOffer,
  deactivateOffer,
  deleteOffer
} from '../services/offers.service'

/**
 * Hook para obtener todas las ofertas
 * @param {Object} filters - Filtros opcionales
 * @returns {Object} { offers, loading, error, refetch }
 */
export const useOffers = (filters = {}) => {
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const filtersKey = JSON.stringify(filters || {})
  const filtersRef = useRef({ key: filtersKey, value: filters || {} })
  if (filtersRef.current.key !== filtersKey) {
    filtersRef.current = { key: filtersKey, value: filters || {} }
  }
  const normalizedFilters = filtersRef.current.value

  const fetchOffers = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getAllOffers(normalizedFilters)
      setOffers(data)
    } catch (err) {
      console.error('Error obteniendo ofertas:', err)
      setError(err.message || 'Error al obtener ofertas')
    } finally {
      setLoading(false)
    }
  }, [normalizedFilters])

  useEffect(() => {
    fetchOffers()
  }, [fetchOffers])

  return {
    offers,
    loading,
    error,
    refetch: fetchOffers
  }
}

/**
 * Hook para obtener una oferta específica
 * @param {string} offerId - ID de la oferta
 * @returns {Object} { offer, loading, error, refetch }
 */
export const useOffer = (offerId) => {
  const [offer, setOffer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchOffer = useCallback(async () => {
    if (!offerId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await getOffer(offerId)
      setOffer(data)
    } catch (err) {
      console.error('Error obteniendo oferta:', err)
      setError(err.message || 'Error al obtener la oferta')
    } finally {
      setLoading(false)
    }
  }, [offerId])

  useEffect(() => {
    fetchOffer()
  }, [fetchOffer])

  return {
    offer,
    loading,
    error,
    refetch: fetchOffer
  }
}

/**
 * Hook para operaciones de ofertas
 * @returns {Object} { create, update, confirm, deactivate, delete, loading }
 */
export const useOfferOperations = () => {
  const [loading, setLoading] = useState(false)

  const create = async (offerData) => {
    try {
      setLoading(true)
      return await createOffer(offerData)
    } catch (error) {
      console.error('Error creando oferta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const update = async (offerId, updates) => {
    try {
      setLoading(true)
      await updateOffer(offerId, updates)
    } catch (error) {
      console.error('Error actualizando oferta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const confirm = async (offerId, userId, userName) => {
    try {
      setLoading(true)
      await confirmOffer(offerId, userId, userName)
    } catch (error) {
      console.error('Error confirmando oferta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deactivate = async (offerId) => {
    try {
      setLoading(true)
      await deactivateOffer(offerId)
    } catch (error) {
      console.error('Error desactivando oferta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const remove = async (offerId) => {
    try {
      setLoading(true)
      await deleteOffer(offerId)
    } catch (error) {
      console.error('Error eliminando oferta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    create,
    update,
    confirm,
    deactivate,
    delete: remove,
    loading
  }
}

